import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { firstValueFrom } from 'rxjs';
import { AuthActions } from '../../core/auth/store/auth.actions';
import { DialogService } from '../../shared/dialog/dialog.service';
import { FcraGateService } from './fcra-gate.service';
import { TncService } from './tnc.service';
import type { TncDialogData, TncDialogResult, TncModalComponent } from './tnc-modal.component';

/**
 * Presents the FCRA / T&C step before the authenticated shell renders (FDD §3.1).
 *
 * FCRA is a login-time step, shown once per login — NOT on every page load. The
 * gate is tracked per login session by FcraGateService (see its docs): once the
 * user has accepted/acknowledged, this guard passes through silently, so ordinary
 * navigation and warm reloads don't re-prompt. But because it runs on the shell's
 * canActivate — the code path a refresh takes back into the app — a reload taken
 * while the modal is still up (session flag not yet set) re-shows FCRA here, before
 * orgGuard ever runs. CallbackComponent resets the flag on each new login so the
 * step is presented again.
 *
 *  - not accepted (first login / new version) → non-dismissable FULL modal, then
 *    POST acceptance;
 *  - already accepted (returning user)        → non-dismissable SHORT acknowledgement.
 *
 * Declining, or any error, fails closed to /signin. Runs after authGuard (needs the
 * bearer token) and before orgGuard (org selection must never precede FCRA).
 */
export const fcraGuard: CanActivateFn = async (): Promise<boolean> => {
  const gate = inject(FcraGateService);
  const tnc = inject(TncService);
  const dialog = inject(DialogService);
  const router = inject(Router);
  const store = inject(Store);

  // Already satisfied this login session → don't re-prompt on ordinary loads.
  if (gate.isSatisfied()) {
    return true;
  }

  try {
    const status = await firstValueFrom(tnc.getStatus());
    const variant = status.accepted ? 'short' : 'full';

    // Lazy-loaded so the modal's Material form deps stay out of the initial bundle.
    // Non-dismissable: Accept/Decline is the only way out, so a refresh can't bypass it.
    const { TncModalComponent: modal } = await import('./tnc-modal.component');
    const ref = dialog.open<TncModalComponent, TncDialogData, TncDialogResult>(modal, {
      data: { variant, tcVersionId: status.tcVersionId, contentText: status.contentText },
      disableClose: true,
      width: '728px',
      mobilePresentation: variant === 'full' ? 'page' : 'modal',
      // Scopes the FCRA-specific mobile padding overrides (see dialog.component.scss).
      panelClass: 'csd-dialog-panel--fcra',
      overlayBackground: 'rgba(242, 242, 242, 0.5)',
      overlayBlur: '25px',
    });

    // The modal records acceptance itself (POST /legal/tc-accept) before it
    // resolves 'accepted', so a failed POST keeps the modal open for a retry
    // instead of dropping the user here — we only see the final decision.
    const result = await firstValueFrom(ref.afterClosed());
    if (result !== 'accepted') {
      // Declining the FCRA terms means the user cannot use the CSD — sign out.
      // The logout effect owns the redirect (IdP end-session, else /signin):
      // navigating here would reach /signin while the tokens are still valid,
      // bounce back into the shell, and race the IdP logout redirect.
      store.dispatch(AuthActions.logout());
      return false;
    }

    gate.markSatisfied();
    return true;
  } catch {
    // Fail closed: never enter the shell if the FCRA gate errored.
    await router.navigate(['/signin'], { replaceUrl: true });
    return false;
  }
};
